/**
 * Budget · Overview (eskiz 18): pul oqimi (hisoblardagi pul, oy kirim/chiqimi, 12 oylik grafik, chiqim tarkibi, to'lanishi
 * kerak -- FinanceCharts), reja va fakt, kategoriyalar, ogohlantirishlar, filiallar jadvali.
 */
import { Link, useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { useBudgetOverview } from "@/features/budget/api";
import { useFinanceOverview } from "@/features/finance/api";
import type { FinanceOverview } from "@/features/finance/types";
import { CashflowChart, CashKpis, DueCard, SpendingDonut } from "./FinanceCharts";
import { money, type BranchSummary, type BudgetAlert, type BudgetCategory, type BudgetOverview, type BudgetSummary } from "@/features/budget/types";
import { periodMonth } from "@/features/dashboards/api";
import { cn, formatMoney } from "@/lib/utils";
import { Card, CardBody, CardHeader, KpiCard } from "@/components/ui/card";
import { ErrorState, PageLoading } from "@/components/ui/misc";
import { Table, Td, Th, Tr } from "@/components/ui/table";
import { BudgetLayout, useBudgetParams } from "./BudgetLayout";
import { ContextMenu } from "@/components/ui/context-menu";
import { branchRowMenu } from "@/features/dashboards/menu";
import { useMe } from "@/features/auth/hooks";
import { can, type Permission } from "@/features/auth/types";
import { useBranchFilter } from "@/app/branchFilter";
import { openInNewTab } from "@/lib/clipboard";

// Matnlar: i18n namespace "budget"
const CATEGORIES: BudgetCategory[] = ["salaries", "expenses", "bonuses"];

export function BudgetPage() {
  const { period, branch, isOwner } = useBudgetParams();
  const overview = useBudgetOverview({ period, branch });
  const finance = useFinanceOverview({ period, branch });
  const month = periodMonth(period);

  return (
    <BudgetLayout>
      {overview.isPending ? (
        <PageLoading />
      ) : overview.isError ? (
        <ErrorState message={overview.error.message} onRetry={() => overview.refetch()} />
      ) : (
        <Overview data={overview.data} finance={finance.data} month={month} period={period} isOwner={isOwner} />
      )}
    </BudgetLayout>
  );
}

function Overview({ data, finance, month, period, isOwner }: { data: BudgetOverview; finance?: FinanceOverview; month: string; period: string; isOwner: boolean }) {
  const { t } = useTranslation("budget");
  const { t: ti } = useTranslation("metrics");
  const total = data.total;
  const over = data.branches.filter((b) => b.over_budget);
  const usedPct = total.used_pct;

  return (
    <div className="space-y-4">
      {!total.plan.exists && (
        <div className="flex items-center justify-between rounded-card border border-budget bg-budget-soft px-4 py-2 text-[13px]">
          <span>
            <span className="font-semibold">{t("overview.noPlan", { month })}</span> {t("overview.noPlanSub")}
          </span>
          <span className="text-[12px] text-muted">{isOwner ? t("overview.noPlanOwner") : t("overview.noPlanManager")}</span>
        </div>
      )}

      {finance && <CashKpis o={finance} month={month} />}
      {finance && (
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
          <CashflowChart o={finance} />
          <SpendingDonut o={finance} month={month} />
        </div>
      )}

      <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
        <KpiCard info={ti("budget.plan")} label={t("overview.planKpi", { month })} value={total.plan.exists ? formatMoney(money(total.plan.total)) : "—"} sub={t("overview.planSub", { count: data.branches.length })} />
        <KpiCard to={`/budget/expenses?period=${period}`} info={ti("budget.actual")} label={t("overview.actual")} value={formatMoney(money(total.actual.total))} sub={usedPct !== null ? t("overview.ofPlan", { pct: usedPct }) : t("overview.noPlanShort")} accent="budget" subTone={usedPct !== null && usedPct > 100 ? "danger" : "muted"} />
        <KpiCard to={`/reports?tab=finance&period=${period}`} info={ti("costPerHire")} label={t("overview.costPerHire")} value={total.cost_per_hire !== null ? formatMoney(total.cost_per_hire) : "—"} sub={t("overview.costSub", { count: total.hires })} />
        <KpiCard
          to={`/reports?tab=branches&period=${period}`}
          info={ti("budget.over")} label={t("overview.over")}
          value={<span className={cn(over.length > 0 && "text-danger")}>{over.length === 0 ? "0" : t("overview.overBranches", { count: over.length })}</span>}
          sub={over.length > 0 ? over.map((b) => b.branch).join(", ") : t("overview.within")}
          subTone={over.length > 0 ? "danger" : "muted"}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[1fr_380px]">
        <Card className="border-budget">
          <CardHeader title={t("overview.byCategory", { month })} />
          <CardBody className="space-y-4">
            {CATEGORIES.map((c) => (
              <CategoryBar key={c} to={`/budget/${c}?period=${period}`} label={t(`cat.${c}`)} actual={money(total.actual[c])} plan={money(total.plan[c])} pct={total.used_by_category[c]} extra={c === "expenses" ? money(total.actual.expenses_pending) : c === "bonuses" ? money(total.actual.bonuses_draft) : 0} extraLabel={c === "expenses" ? t("overview.pending") : t("overview.draft")} />
            ))}
          </CardBody>
        </Card>
        <div className="space-y-4">
          {finance && <DueCard o={finance} period={period} />}
          <Card>
            <CardHeader title={t("overview.alerts")} />
            <CardBody>
              {data.alerts.length === 0 ? (
                <p className="text-[13px] text-muted">{t("overview.noAlerts")}</p>
              ) : (
                <ul className="space-y-2">
                  {data.alerts.map((a, i) => (
                    <AlertRow key={`${a.kind}-${a.branch_id}-${i}`} alert={a} period={period} />
                  ))}
                </ul>
              )}
            </CardBody>
          </Card>
        </div>
      </div>

      <Card>
        <CardHeader title={t("overview.branches", { month })} subtitle={t("overview.branchesSub")} />
        <CardBody className="px-0 pb-0">
          <BranchesTable rows={data.branches} total={total} />
        </CardBody>
      </Card>
    </div>
  );
}

function CategoryBar({ label, actual, plan, pct, extra, extraLabel, to }: { label: string; actual: number; plan: number; pct: number | null; extra: number; extraLabel: string; to: string }) {
  const { t } = useTranslation("budget");
  const overs = pct !== null && pct > 100;
  const width = pct === null ? (actual > 0 ? 100 : 0) : Math.min(100, pct);
  return (
    <div>
      <div className="mb-1 flex items-center justify-between text-[13px]">
        <Link to={to} className="font-semibold hover:underline">
          {label}
        </Link>
        <span className={cn(overs ? "font-semibold text-danger" : "text-ink-soft")}>
          {formatMoney(actual)} {plan > 0 ? t("overview.of", { plan: formatMoney(plan), pct }) : t("overview.noPlanBar")}
          {extra > 0 && <span className="ml-2 text-[11px] text-muted">{t("overview.extra", { amount: formatMoney(extra), label: extraLabel })}</span>}
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-fill">
        <div className={cn("h-full rounded-full", overs ? "bg-danger" : "bg-budget")} style={{ width: `${width}%` }} />
      </div>
    </div>
  );
}

function alertLink(a: BudgetAlert, period: string): string {
  if (a.link) return a.link.includes("?") ? `${a.link}&period=${period}` : `${a.link}?period=${period}`;
  return `/budget/expenses?period=${period}`;
}

function AlertRow({ alert, period }: { alert: BudgetAlert; period: string }) {
  const { t } = useTranslation("budget");
  const danger = alert.kind === "over_budget" || alert.kind === "over_category";
  return (
    <li className="flex items-start justify-between gap-3 text-[13px]">
      <span className="flex items-start gap-2">
        <span className={cn("mt-1.5 inline-block h-2 w-2 shrink-0 rounded-full", danger ? "bg-danger" : "bg-budget")} />
        <span>{alert.message}</span>
      </span>
      <Link to={alertLink(alert, period)} className="shrink-0 text-[12px] text-primary hover:underline">
        {danger ? t("overview.details") : t("overview.review")}
      </Link>
    </li>
  );
}

function UsedCell({ pct }: { pct: number | null }) {
  if (pct === null) return <span className="text-muted">—</span>;
  const over = pct > 100;
  return (
    <span className="flex items-center gap-2">
      <span className="h-1.5 w-16 overflow-hidden rounded-full bg-fill">
        <span className={cn("block h-full rounded-full", over ? "bg-danger" : "bg-budget")} style={{ width: `${Math.min(100, pct)}%` }} />
      </span>
      <span className={cn(over && "font-semibold text-danger")}>{pct}%</span>
    </span>
  );
}

function BranchesTable({ rows, total }: { rows: BranchSummary[]; total: BudgetSummary }) {
  const { t } = useTranslation("budget");
  const { t: tm } = useTranslation("menu");
  const navigate = useNavigate();
  const me = useMe();
  const { setBranchId } = useBranchFilter();
  // Qator: bosilsa shu filial doirasi (yuqoridagi tanlov kabi), o'ng tugma -- filial menyusi (bosh sahifadagi bilan bir xil)
  const paths = { navigate: (path: string) => navigate(path), openNewTab: openInNewTab, can: (perm: Permission) => (me.data ? can(me.data, perm) : false) };
  const rowMenu = (id: number) =>
    branchRowMenu(
      { branch_id: id },
      {
        ...paths,
        openDashboard: () => {
          setBranchId(id);
          navigate("/");
        },
        openBudget: () => setBranchId(id),
      },
      tm,
    );
  const cell = (s: BudgetSummary, isTotal = false) => (
    <>
      <Td className="text-right">{s.plan.exists ? formatMoney(money(s.plan.total)) : <span className="text-muted">—</span>}</Td>
      <Td className={cn("text-right", s.over_budget && "font-semibold text-danger")}>{formatMoney(money(s.actual.total))}</Td>
      <Td className="bg-budget-soft/60">
        <UsedCell pct={s.used_pct} />
      </Td>
      <Td className="text-right">{formatMoney(money(s.actual.salaries))}</Td>
      <Td className="text-right">{formatMoney(money(s.actual.expenses))}</Td>
      <Td className="text-right">{formatMoney(money(s.actual.bonuses))}</Td>
      <Td className="text-right">{s.hires}</Td>
      <Td className={cn("bg-budget-soft/60 text-right", isTotal && "font-bold")}>{s.cost_per_hire !== null ? formatMoney(s.cost_per_hire) : <span className="text-muted">—</span>}</Td>
    </>
  );
  return (
    <Table variant="flush">
      <thead>
        <tr>
          <Th>{t("overview.cols.branch")}</Th>
          <Th className="text-right">{t("overview.cols.plan")}</Th>
          <Th className="text-right">{t("overview.cols.actual")}</Th>
          <Th>{t("overview.cols.used")}</Th>
          <Th className="text-right">{t("overview.cols.salaries")}</Th>
          <Th className="text-right">{t("overview.cols.expenses")}</Th>
          <Th className="text-right">{t("overview.cols.bonuses")}</Th>
          <Th className="text-right">{t("overview.cols.hires")}</Th>
          <Th className="text-right">{t("overview.cols.costPerHire")}</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <ContextMenu key={r.branch_id ?? r.branch} items={() => (r.branch_id == null ? [] : rowMenu(r.branch_id))} disabled={r.branch_id == null}>
            <Tr className={cn(r.branch_id != null && "cursor-pointer")} onClick={() => r.branch_id != null && setBranchId(r.branch_id)}>
              <Td className="font-semibold">{r.branch}</Td>
              {cell(r)}
            </Tr>
          </ContextMenu>
        ))}
        {rows.length > 1 && (
          <Tr className="bg-fill/50 font-semibold">
            <Td className="font-bold">{t("total")}</Td>
            {cell(total, true)}
          </Tr>
        )}
      </tbody>
    </Table>
  );
}
